/**
 * Blocks in server mode: the player's digging and right-clicks on blocks as
 * intents (`dig` start / abort / finish, `useOn`), predicted locally with the
 * same rules the server runs (game/blockActions.ts) and put back when the
 * server's `ack` refuses them (ServerSession.track).
 *
 * The world is predicted, and the count of the held stack a survival action
 * uses up (`consumeHeld`). The rest of the inventory, drops, XP, tool wear and
 * anything rolled (bone meal) are the server's and arrive as facts. Blocks and
 * items with a server-side hook (containers, beds, fences, spawn eggs, boats)
 * are sent unpredicted. Sounds and particles of the player's own actions play
 * here at once; the server sends `fx` `b.*` for everybody else's.
 */

import { IS_TARGETABLE } from '../blocks';
import { I } from '../game/itemIds';
import {
  breakBlock, faceOf, interactableBlock, togglable, useOnBlock,
  type BlockActor, type BlockEnv, type BlockFx, type BlockHit,
} from '../game/blockActions';
import { isEmpty } from '../game/types';
import type { World } from '../world/chunk';
import type { DimensionId } from '../world/dimension';
import type { ServerSession } from './serverSession';

/** The part of ChunkManager this needs. */
export interface BlocksWorld {
  readonly world: World;
  readonly dimension: DimensionId;
  canEdit(x: number, y: number, z: number): boolean;
  /** Edit locally with the block rules; returns [x, y, z, oldId, ...] of the cells it changed. */
  predictBlock(x: number, y: number, z: number, id: number): number[];
  batchEdits(fn: () => void): void;
}

export interface BlocksClientDeps {
  srv: ServerSession;
  chunks: BlocksWorld;
  /** The local player as the block rules see it (only read: position, yaw, held item, mode). */
  actor: BlockActor;
  /** Sound and particles of a block action (also used for other players' `b.*` fx). */
  fx(kind: BlockFx, x: number, y: number, z: number, d: number): void;
  /** Would a block in this cell overlap the local player? */
  occupied(x: number, y: number, z: number, id: number): boolean;
  toast(text: string): void;
  /** A block the server has its own right-click hook for, beyond the interactable ones (fences). */
  serverBlock?(id: number): boolean;
  /** A held item the server has its own use-on-block hook for (spawn eggs, boats). */
  serverItem?(id: number): boolean;
  /**
   * Survival: the action used up `n` of the held stack. Shown at once (the hotbar count would otherwise
   * trail the predicted block by a round trip); client/windows.ts puts the server's count back over it.
   */
  consumeHeld?(n: number): void;
}

export interface BlocksClient {
  /** Every frame: the block being dug (button held on it), or null. Sends dig start / abort as it changes. */
  dig(hit: BlockHit | null): void;
  /** The local dig timer ran out: predict the break and ask the server to finish it. */
  digFinish(hit: BlockHit): void;
  /**
   * A right-click. `hit` is the targeted block, `click` the point of its face
   * that was clicked (world space), `fluid` the ray an empty bucket / bottle
   * uses (stops at water and lava too). Returns true when the click was used
   * (and sent); false leaves it to the rest of the item chain.
   */
  useOn(hit: BlockHit | null, click: readonly [number, number, number] | null, sneaking: boolean, fluid: () => BlockHit | null): boolean;
}

const FX_KINDS = new Set<string>(['break', 'place', 'door', 'ignite', 'bucket', 'fizz', 'bonemeal', 'note', 'click', 'piston']);
const NOOP = (): void => { /* the server's to do */ };

export function installBlocks(d: BlocksClientDeps): BlocksClient {
  const { srv, chunks } = d;

  // everybody else's block sounds (ours were played when we predicted them)
  srv.on('fx', (m) => {
    if (typeof m.k !== 'string' || !m.k.startsWith('b.')) return;
    const kind = m.k.slice(2);
    if (FX_KINDS.has(kind)) d.fx(kind as BlockFx, m.x, m.y, m.z, typeof m.d === 'number' ? m.d : 0);
  });

  /** Predicted cells of the action being run: the first snapshot of a cell is its value before. */
  let cells: number[] = [];
  const seen = new Set<string>();
  const env: BlockEnv = {
    get world() { return chunks.world; },
    get dim() { return chunks.dimension; },
    canEdit: (x, y, z) => chunks.canEdit(x, y, z),
    setBlock: (x, y, z, id) => {
      const c = chunks.predictBlock(x, y, z, id);
      for (let i = 0; i < c.length; i += 4) {
        const k = c[i] + ',' + c[i + 1] + ',' + c[i + 2];
        if (seen.has(k)) continue;
        seen.add(k);
        cells.push(c[i], c[i + 1], c[i + 2], c[i + 3]);
      }
    },
    rnd: Math.random,
    dropBlock: NOOP, dropXp: NOOP, primeTnt: NOOP, igniteFire: NOOP,
    fx: (k, x, y, z, v) => d.fx(k, x, y, z, v),
    occupied: (x, y, z, id) => d.occupied(x, y, z, id),
    toast: (t) => d.toast(t),
    batch: (fn) => chunks.batchEdits(fn),
    predict: true,
  };
  const a = d.actor;
  const actor: BlockActor = {
    get x() { return a.x; }, get y() { return a.y; }, get z() { return a.z; }, get yaw() { return a.yaw; },
    get creative() { return a.creative; }, get held() { return a.held; }, get helmet() { return a.helmet; },
    get effects() { return a.effects; },
    consumeHeld: (n: number) => { if (!a.creative) d.consumeHeld?.(n); }, wearHeld: NOOP, swapHeld: NOOP, give: NOOP, exhaust: NOOP,
  };
  /** Run one predicted action; returns the seq its cells are tracked under. */
  const predicted = <T>(fn: () => T): [T, number] => {
    cells = []; seen.clear();
    const r = fn();
    const seq = srv.nextSeq();
    if (cells.length > 0) srv.track(seq, cells);
    cells = [];
    return [r, seq];
  };
  const face = (h: BlockHit): number => { const f = faceOf(h.nx, h.ny, h.nz); return f >= 0 && f < 6 ? f : 2; };

  let cur: { x: number; y: number; z: number } | null = null;
  const abort = (): void => {
    if (!cur) return;
    srv.send({ t: 'dig', seq: srv.nextSeq(), a: 1, x: cur.x, y: cur.y, z: cur.z });
    cur = null;
  };

  return {
    dig(hit) {
      if (!hit) { abort(); return; }
      if (cur && cur.x === hit.x && cur.y === hit.y && cur.z === hit.z) return;
      abort();
      cur = { x: hit.x, y: hit.y, z: hit.z };
      srv.send({ t: 'dig', seq: srv.nextSeq(), a: 0, x: hit.x, y: hit.y, z: hit.z, face: face(hit) });
    },

    digFinish(hit) {
      const [, seq] = predicted(() => breakBlock(env, actor, hit.x, hit.y, hit.z));
      srv.send({ t: 'dig', seq, a: 2, x: hit.x, y: hit.y, z: hit.z, face: face(hit) });
      cur = null; // a refused finish is started again by the next frame's dig()
    },

    useOn(hit, click, sneaking, fluid) {
      const heldId = isEmpty(a.held) ? 0 : a.held.id;
      let h = hit, c = click;
      if (heldId === I.BUCKET || heldId === I.GLASS_BOTTLE) {
        const f = fluid();
        // the nearer fluid replaces the block behind it; a plain block keeps its click point
        if (f && (!hit || f.x !== hit.x || f.y !== hit.y || f.z !== hit.z)) { h = f; c = null; }
      }
      if (!h) return false;
      if (IS_TARGETABLE[h.id] !== 1 && h === hit) return false;
      const at = c ?? [h.x + 0.5, h.y + 0.5, h.z + 0.5];
      const [done, seq] = predicted(() => useOnBlock(env, actor, h, at, {
        sneaking,
        blockUse: (_x, _y, _z, id) => (interactableBlock(id) && !togglable(id)) || d.serverBlock?.(id) === true,
        itemUse: () => d.serverItem?.(heldId) === true,
      }));
      if (!done) return false;
      const frac = (v: number, o: number): number => Math.min(1, Math.max(0, v - o));
      srv.send({ t: 'useOn', seq, x: h.x, y: h.y, z: h.z, face: face(h), hx: frac(at[0], h.x), hy: frac(at[1], h.y), hz: frac(at[2], h.z), hand: 0 });
      return true;
    },
  };
}
